// AdminOcrIntake.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./PawnerForm.css";

/** NEW admin scan endpoints (do not overlap existing) */
const ADMIN_SCAN_NRIC = "https://www.lianfoong.com/api/admin-intake/scan/nric";
const ADMIN_SCAN_PPT  = "https://www.lianfoong.com/api/admin-intake/scan/passport";

/** Existing submission endpoints (unchanged) */
const CUSTOMERS_ENDPOINT = "https://www.lianfoong.com/api/customers";
const UPDATE_TXN_ENDPOINT = "https://www.lianfoong.com/api/pawn-transactions/update";
const CREATE_TXN_ENDPOINT = "https://www.lianfoong.com/api/pawn-transactions";

const MOBILE_RE_8 = /^\d{8}$/;
const NRIC_RE = /^[STFGM]\d{7}[A-Z]$/i;

export default function AdminOcrIntake({ transactionData }) {
  const [mode, setMode] = useState("IC"); // "IC" | "PASSPORT"
  const [front, setFront] = useState(null);
  const [back,  setBack]  = useState(null);
  const [pp,    setPp]    = useState(null);

  const [frontURL, setFrontURL] = useState("");
  const [backURL,  setBackURL]  = useState("");
  const [ppURL,    setPpURL]    = useState("");

  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState("");

  // Unified customer form fields (same keys your existing APIs expect)
  const [f, setF] = useState({
    nric: "",           // required to submit
    name: "",
    dob: "",
    nationality: "",
    race: "",
    sex: "",
    sgAddr: "",
    mobile: "",
    blacklisted: false,
  });

  const pickFile = (file, setFile, setPreview) => {
    if (!file) { setFile(null); setPreview(""); return; }
    if (!["image/jpeg","image/jpg","image/png","image/webp"].includes(file.type)) {
      alert("Only JPG/PNG/WEBP allowed."); return;
    }
    if (file.size > 12 * 1024 * 1024) {
      alert("Max file size is 12MB."); return;
    }
    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

const navigate = useNavigate();
const goBack = () => navigate("/dashboard"); // adjust path if your dashboard route differs

useEffect(() => {
  const onKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      goBack();
    }
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}, []);

  const scan = async () => {
    setMsg("");
    try {
      setLoading(true);
      if (mode === "IC") {
        if (!front || !back) { alert("Please attach BOTH front and back images."); return; }
        const fd = new FormData();
        fd.append("front", front);
        fd.append("back",  back);
        const resp = await axios.post(ADMIN_SCAN_NRIC, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          validateStatus: s => s < 500
        });
        if (resp.data?.error) {
          // 422 mismatch -> do not populate
          setMsg(resp.data.error);
          alert(resp.data.error);
          return;
        }
        const d = resp.data || {};
        setF(cur => ({
          ...cur,
          nric: (d.nric || "").toUpperCase(),
          name: d.name || "",
          dob: d.dob || "",
          nationality: d.nationality || "",
          race: d.race || "",
          sex: d.sex || "",
          sgAddr: d.address || "",
        }));
        setMsg("IC scanned successfully. Please confirm and add mobile.");
      } else {
        if (!pp) { alert("Please attach the Passport photo page."); return; }
        const fd = new FormData();
        fd.append("photoPage", pp);
        const { data } = await axios.post(ADMIN_SCAN_PPT, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        // Merge only visible non-empty fields into the form
        setF(cur => {
          const out = { ...cur };
          const put = (k,v) => { if (typeof v === "string" && v.trim() !== "") out[k] = v.trim(); };
          put("name",        data.name);
          put("dob",         data.dob);
          put("nationality", data.nationality);
          put("sex",         data.sex);
          put("sgAddr",      data.address); // only if the passport page shows it
          return out;
        });
        setMsg("Passport scanned. Visible details added to the form. (NRIC remains empty.)");
      }
    } catch (e) {
      console.error(e);
      alert("Scan failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    // only allow submit when we actually have NRIC (DB contract expects nirc)
    const missing = [];
    if (!f.nric) missing.push("NRIC");
    if (!f.name) missing.push("Name");
    if (!f.dob) missing.push("Birthdate");
    if (!f.nationality) missing.push("Nationality");
    if (!f.sgAddr) missing.push("Address (Singapore)");
    if (!f.mobile) missing.push("Mobile");

    if (missing.length) {
      alert("Please fill the following:\n• " + missing.join("\n• "));
      return;
    }
    if (!NRIC_RE.test(f.nric)) {
      alert("NRIC format looks off (e.g., S1234567D)."); return;
    }
    if (!MOBILE_RE_8.test(f.mobile)) {
      alert("Please enter a valid 8-digit Singapore mobile number."); return;
    }

    try {
      // 1) Upsert customer (same shape as your existing code)
      await axios.post(CUSTOMERS_ENDPOINT, {
        nirc: f.nric,
        name: f.name,
        race: f.race,
        sex: f.sex,
        nationality: f.nationality,
        sgAddr: f.sgAddr,
        dob: f.dob,
        contactNoMobile: f.mobile,
        blacklisted: f.blacklisted,
      });

      // 2) Update/Create transaction with same contract
      if (transactionData?.receiptNo) {
        await axios.put(
          `${UPDATE_TXN_ENDPOINT}?receiptNo=${transactionData.receiptNo}`,
          { ...transactionData, pawnerNirc: f.nric }
        );
      } else if (transactionData) {
        const resp = await axios.post(
          CREATE_TXN_ENDPOINT,
          {
            ...transactionData,
            pawnerNirc: f.nric,
            amount: Number(transactionData.principal),
            valuationAmount: Number(transactionData.valuation),
            item: transactionData.items,
            weight: transactionData.weight,
            remark: transactionData.remark,
            noOfItem: transactionData.noOfItem,
            grantDate: transactionData.grantDate,
            expiryDate: transactionData.expiryDate,
            activeRecord: true,
          },
          { responseType: "blob" }
        );
        const blob = new Blob([resp.data], { type: "application/pdf" });
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "receipt.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }

      alert("Saved successfully.");
      setMsg("");
      setF({ nric:"", name:"", dob:"", nationality:"", race:"", sex:"", sgAddr:"", mobile:"", blacklisted:false });
    } catch (e) {
      console.error(e);
      alert("Save failed.");
    }
  };

  return (
    <div className="pawner-form-container">
      <h2 className="form-title">Admin OCR Intake (Fallback)</h2>

      {/* Mode toggle */}
      <div className="button-group" style={{ marginBottom: 12 }}>
        <button className={`ok-button ${mode === "IC" ? "active" : ""}`} onClick={() => setMode("IC")}>
          Singapore NRIC
        </button>
        <button className={`ok-button ${mode === "PASSPORT" ? "active" : ""}`} onClick={() => setMode("PASSPORT")}>
          Passport (Photo Page)
        </button>
      </div>

      {/* Uploads */}
      {mode === "IC" ? (
        <div className="form-row">
          <div className="form-group">
            <label>NRIC Front:</label>
            <input type="file" accept="image/*" onChange={(e)=>pickFile(e.target.files?.[0]??null,setFront,setFrontURL)} />
            {frontURL && <img src={frontURL} alt="front" style={{ marginTop: 6, maxWidth: 200, borderRadius: 6 }} />}
          </div>
          <div className="form-group">
            <label>NRIC Back:</label>
            <input type="file" accept="image/*" onChange={(e)=>pickFile(e.target.files?.[0]??null,setBack,setBackURL)} />
            {backURL && <img src={backURL} alt="back" style={{ marginTop: 6, maxWidth: 200, borderRadius: 6 }} />}
          </div>
        </div>
      ) : (
        <div className="form-group">
          <label>Passport Photo Page:</label>
          <input type="file" accept="image/*" onChange={(e)=>pickFile(e.target.files?.[0]??null,setPp,setPpURL)} />
          {ppURL && <img src={ppURL} alt="passport" style={{ marginTop: 6, maxWidth: 240, borderRadius: 6 }} />}
        </div>
      )}

      <div className="form-group">
        <button className="ok-button" disabled={loading} onClick={scan}>
          {loading ? "Scanning…" : "Scan"}
        </button>
      </div>

      {msg && <p style={{ color: "#555", marginTop: -2 }}>{msg}</p>}

      {/* Admin-editable customer fields (same as your DB contract) */}
      <div className="form-group">
        <label>NRIC:</label>
        <input className="input-field" value={f.nric} onChange={(e)=>setF({...f, nric: e.target.value.toUpperCase()})} />
      </div>
      <div className="form-group">
        <label>Name:</label>
        <input className="input-field" value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Birthdate:</label>
          <input type="date" className="input-field" value={f.dob} onChange={(e)=>setF({...f, dob: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Nationality:</label>
          <input className="input-field" value={f.nationality} onChange={(e)=>setF({...f, nationality: e.target.value})} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Race:</label>
          <input className="input-field" value={f.race} onChange={(e)=>setF({...f, race: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Sex:</label>
          <input className="input-field" value={f.sex} onChange={(e)=>setF({...f, sex: e.target.value})} />
        </div>
      </div>

      <div className="form-group">
        <label>Address (Singapore):</label>
        <textarea className="input-field" value={f.sgAddr} onChange={(e)=>setF({...f, sgAddr: e.target.value})} />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Mobile (8-digit):</label>
          <input className="input-field" placeholder="e.g., 91234567"
                 value={f.mobile} onChange={(e)=>setF({...f, mobile: e.target.value.replace(/\s+/g,"")})} />
        </div>
        <div className="form-group checkbox-group" style={{ alignItems: "center" }}>
          <input type="checkbox" id="blacklisted" checked={f.blacklisted} onChange={(e)=>setF({...f, blacklisted: e.target.checked})} />
          <label htmlFor="blacklisted">Blacklisted</label>
        </div>
      </div>

      <div className="button-group" style={{ gap: 8 }}>
        <button
          className="ok-button"
          onClick={submit}
          disabled={!f.nric}  // submit allowed only when an NRIC is provided
          title={!f.nric ? "Submission requires NRIC (passport scan only pre-fills visible details)" : ""}
        >
          Submit
        </button>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
  <button className="cancel-button" onClick={goBack}> Back  [Esc]</button>
</div>
    </div>
  );
}
